import { useCallback } from "react";
import watermarkLandscape from "../assets/WatermarkOverlay_Landscape.png";
import watermarkPortrait from "../assets/WatermarkOverlay_Portrait.png";
import { WatermarkOptions } from "../types";

const DEFAULT_WATERMARK_OPTIONS = {
  position: "bottom-right",
  opacity: 0.5,
} satisfies WatermarkOptions;

export const useWatermark = () => {
  const addImageWatermark = useCallback(
    async (file: File, options: WatermarkOptions = {}): Promise<File> => {
      const finalOptions = { ...DEFAULT_WATERMARK_OPTIONS, ...options };

      return new Promise((resolve, reject) => {
        const originalImage = new Image();
        const watermarkImage = new Image();
        let originalImageUrl: string | null = null;

        originalImage.crossOrigin = "anonymous";
        watermarkImage.crossOrigin = "anonymous";

        let imagesLoaded = 0;
        const totalImages = 2;
        let hasTimedOut = false;

        // Cleanup function to revoke blob URLs
        const cleanup = () => {
          if (originalImageUrl) {
            URL.revokeObjectURL(originalImageUrl);
          }
        };

        // Timeout handling (10 seconds)
        const timeoutId = setTimeout(() => {
          hasTimedOut = true;
          cleanup();
          reject(new Error("Image loading timed out"));
        }, 10000);

        const onImageLoad = () => {
          if (hasTimedOut) return;

          imagesLoaded++;
          if (imagesLoaded === totalImages) {
            clearTimeout(timeoutId);

            try {
              // Validate image dimensions
              if (originalImage.width === 0 || originalImage.height === 0) {
                cleanup();
                reject(new Error("Invalid image dimensions"));
                return;
              }

              if (watermarkImage.width === 0 || watermarkImage.height === 0) {
                cleanup();
                reject(new Error("Invalid watermark dimensions"));
                return;
              }

              // Create a canvas with the same dimensions as the original image
              const canvas = document.createElement("canvas");
              canvas.width = originalImage.width;
              canvas.height = originalImage.height;
              const ctx = canvas.getContext("2d");

              if (!ctx) {
                cleanup();
                reject(new Error("Failed to create canvas context"));
                return;
              }

              // Draw the original image
              ctx.drawImage(
                originalImage,
                0,
                0,
                originalImage.width,
                originalImage.height
              );

              // Calculate overlay dimensions to cover entire canvas
              const coverDimensions = calculateCoverDimensions(
                originalImage.width,
                originalImage.height,
                watermarkImage.width,
                watermarkImage.height
              );

              // Apply watermark opacity
              ctx.globalAlpha = finalOptions.opacity;

              // Draw the watermark overlay covering entire canvas
              ctx.drawImage(
                watermarkImage,
                coverDimensions.x,
                coverDimensions.y,
                coverDimensions.width,
                coverDimensions.height
              );

              // Reset opacity for future operations
              ctx.globalAlpha = 1.0;
              canvas.toBlob(
                (blob) => {
                  cleanup();
                  if (blob) {
                    const watermarkedFile = new File([blob], file.name, {
                      type: file.type,
                      lastModified: Date.now(),
                    });
                    resolve(watermarkedFile);
                  } else {
                    reject(new Error("Failed to create watermarked image"));
                  }
                },
                file.type,
                0.8
              ); // 80% quality
            } catch (error) {
              cleanup();
              console.error("Error creating watermark:", error);
              reject(error);
            }
          }
        };

        const onImageError = (imageName: string) => () => {
          if (hasTimedOut) return;

          clearTimeout(timeoutId);
          cleanup();
          reject(
            new Error(
              `Failed to load ${imageName} image. Please check the image file is valid.`
            )
          );
        };

        // Set up handlers
        originalImage.onerror = onImageError("original");
        watermarkImage.onload = onImageLoad;
        watermarkImage.onerror = onImageError("watermark");

        // Load original image first to determine aspect ratio
        originalImage.onload = () => {
          if (hasTimedOut) return;

          const isLandscape = originalImage.width >= originalImage.height;
          watermarkImage.src = isLandscape
            ? watermarkLandscape
            : watermarkPortrait;
          onImageLoad();
        };

        originalImageUrl = URL.createObjectURL(file);
        originalImage.src = originalImageUrl;
      });
    },
    []
  );

  return { addImageWatermark };
};

function calculateCoverDimensions(
  canvasWidth: number,
  canvasHeight: number,
  overlayWidth: number,
  overlayHeight: number
): { width: number; height: number; x: number; y: number } {
  // Calculate aspect ratios
  const canvasAspect = canvasWidth / canvasHeight;
  const overlayAspect = overlayWidth / overlayHeight;

  let width: number;
  let height: number;
  let x: number;
  let y: number;

  if (canvasAspect > overlayAspect) {
    // Canvas is wider than overlay - fit to width
    width = canvasWidth;
    height = canvasWidth / overlayAspect;
    x = 0;
    y = (canvasHeight - height) / 2;
  } else {
    // Canvas is taller than overlay - fit to height
    height = canvasHeight;
    width = canvasHeight * overlayAspect;
    x = (canvasWidth - width) / 2;
    y = 0;
  }

  return { width, height, x, y };
}
